import { type FC, useCallback, useEffect, useMemo } from 'react';

import { EmptyState } from '@vakantiesnl/components/src/molecules/AccommodationEmptyState/EmptyState';
import { Pagination } from '@vakantiesnl/components/src/molecules/Pagination';
import { ProductCardWrapper } from '@vakantiesnl/components/src/molecules/ProductCard/ProductCardWrapper';
import { DEFAULT_DEALS_PAGE_SIZE } from '@vakantiesnl/components/src/organisms/Flashdeals/constants';
import { getGeoEntitiesFromSlugs } from '@vakantiesnl/components/src/utils/geo';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useMapGeo } from '@vakantiesnl/services/src/gtm/util/useMapGeo';
import { useGetFlashDeals, useGetGeo } from '@vakantiesnl/services/src/hooks/queries';
import { useGetCompositeSearchParams } from '@vakantiesnl/services/src/hooks/useGetCompositeSearchParams';
import { useFilterStore } from '@vakantiesnl/services/src/stores/filtersStore';
import { type Search } from '@vakantiesnl/types';

import { useStyles } from './AccommodationList.style';
import { AccommodationListSkeleton } from './AccommodationListSkeleton';

type Props = {
	accommodations: Search.CompactAccommodationItem[];
	total: number;
	isLoading: boolean;
};

export const AccommodationList: FC<Props> = ({ accommodations, total, isLoading }) => {
	const microCopies = useMicroCopyContext();
	const geo = useGetGeo();

	const filters = useFilterStore((s) => s.filters);
	const noOfFilters = useFilterStore((s) => s.getSelectedFilterCount());
	const setPage = useFilterStore((s) => s.setPage);

	const { classes } = useStyles();

	const params = useGetCompositeSearchParams();

	const { flashDeals, isFlashDealsLoading } = useGetFlashDeals(
		{
			...params,
			page_size: DEFAULT_DEALS_PAGE_SIZE,
			page: 1,
		},
		1,
	);

	const flashDeal = useMemo(() => flashDeals[0], [flashDeals]);

	const { country, region, city } = getGeoEntitiesFromSlugs(filters, geo);

	const setPageFn = useCallback(
		(value: number) => {
			setPage(value);
			if (window && window.scrollTo) {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			}
		},
		[setPage],
	);

	const GTMformatedGeo = useMapGeo({
		country: country?.map((country) => country.name).toString(),
		region: region?.map((region) => region.name).toString(),
		city: city?.map((city) => city.name).toString(),
	});

	useEffect(() => {
		if (accommodations && !isLoading) {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/search').then(({ searchPageView }) => {
					track(
						searchPageView(
							total,
							filters.page,
							filters.size,
							filters.sort,
							accommodations?.map((accommodation) => String(accommodation?.giataId)),
							filters as unknown as Record<string, unknown>,
							{
								entity_level: 'destination',
								entity_id: '',
							},
							GTMformatedGeo,
							noOfFilters,
						),
					);
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	/** Show skeleton then AccoList is isLoading */
	if (isLoading || isFlashDealsLoading) {
		return (
			<div className={classes.accoListContainer}>
				<AccommodationListSkeleton />
			</div>
		);
	}

	/** When no results show the EmptyState component */
	if (!isLoading && total === 0) return <EmptyState microCopies={microCopies} />;
	return (
		<div className={classes.accoListContainer}>
			{!!flashDeal && (
				<div className={classes.accoTile} key={flashDeal.accommodation.title}>
					<ProductCardWrapper
						key={flashDeal.accommodation.title}
						accommodation={flashDeal.accommodation}
						flashDeal={flashDeal.flashDeal}
						position={0} // flash deal is always on the first position
						pageType="listing"
						size="large"
						filters={filters}
						needsDurationOverride
					/>
				</div>
			)}
			{accommodations?.map((accommodation, index) => (
				<div className={classes.accoTile} key={accommodation.title}>
					<ProductCardWrapper
						key={index}
						accommodation={accommodation}
						position={index + 1}
						pageType="listing"
						size="large"
						filters={filters}
					/>
				</div>
			))}
			<Pagination total={total} pageSize={filters.size} page={filters.page} setPageFn={setPageFn} />
		</div>
	);
};
